import { useEffect, useState } from 'react'

import { isNil } from 'ramda'

import { getGlobalFiltersSettings, setGlobalFiltersSettings } from 'api/AccountActivityAPI'
import { GlobalFilterSetting } from 'api/AccountActivityAPI/types'
import { segmentTrack, TrackType } from 'utils/tracking'

import useNotifications from './useNotifications'

export interface GlobalFilterCondition {
  filterOperation: string
  attribute: string
  values: string[]
  comparisonType: string
}

export const EMPTY_CONDITION = {
  filterOperation: '',
  attribute: '',
  values: [],
  comparisonType: 'STRING'
}

export enum GlobalFilterSettingType {
  IN_CRM = 'in-crm',
  EXISTING_CUSTOMER = 'existing-customer',
  DISQUALIFIED = 'recently-disqualified'
}

const EMPTY_DATA = [
  {
    id: null,
    name: GlobalFilterSettingType.IN_CRM,
    isEnabled: true,
    value: {}
  },
  {
    id: null,
    name: GlobalFilterSettingType.EXISTING_CUSTOMER,
    isEnabled: false,
    value: EMPTY_CONDITION
  },
  {
    id: null,
    name: GlobalFilterSettingType.DISQUALIFIED,
    isEnabled: false,
    value: EMPTY_CONDITION
  }
]

export const useAdvancedPreferences = (setFiltersUpdated: (updated: boolean) => void) => {
  const { success } = useNotifications()
  const [defaultData, setDefaultData] = useState<GlobalFilterSetting[]>(EMPTY_DATA)
  const [inCRM, setInCRM] = useState<boolean>()
  const [hideCurrent, setHideCurrent] = useState<boolean>()
  const [currentCustomerCondition, setCurrentCustomerCondition] = useState<GlobalFilterCondition>()
  const [hideDisqualified, setHideDisqualified] = useState<boolean>()
  const [disqualifiedCondition, setDisqualifiedCondition] = useState<GlobalFilterCondition>()

  const [showSaveCancel, setShowSaveCancel] = useState<boolean>(false)
  const [invalidState, setInvalidState] = useState<boolean>(false)

  const parseSettings = (settings: GlobalFilterSetting[]) => {
    settings.forEach(setting => {
      switch (setting.name) {
        case GlobalFilterSettingType.IN_CRM:
          if (isNil(setting.isEnabled)) {
            setInCRM(true)
          } else {
            setInCRM(setting.isEnabled)
          }
          break
        case GlobalFilterSettingType.EXISTING_CUSTOMER:
          setHideCurrent(setting.isEnabled)
          setCurrentCustomerCondition(setting.value)
          break
        case GlobalFilterSettingType.DISQUALIFIED:
          setHideDisqualified(setting.isEnabled)
          setDisqualifiedCondition(setting.value)
          break
        default:
          break
      }
    })
  }

  useEffect(() => {
    getGlobalFiltersSettings().then(response => {
      if (response.data.length > 0) {
        setDefaultData(response.data)
        parseSettings(response.data)
      } else {
        parseSettings(EMPTY_DATA)
      }
    })
  }, [])

  const buildRequest = () => {
    // iterate over default data to make sure we have the ID for an update, but apply any new setting values
    const requestBody = defaultData.map(setting => {
      switch (setting.name) {
        case GlobalFilterSettingType.IN_CRM:
          return {
            ...setting,
            isEnabled: inCRM
          }
        case GlobalFilterSettingType.EXISTING_CUSTOMER:
          return {
            ...setting,
            value: currentCustomerCondition,
            isEnabled: hideCurrent
          }
        case GlobalFilterSettingType.DISQUALIFIED:
          return {
            ...setting,
            value: disqualifiedCondition,
            isEnabled: hideDisqualified
          }
        default:
          return {}
      }
    })

    return requestBody
  }

  const validateToSave = () => {
    let currentCustomerInvalid = false
    let disqualifiedInvalid = false
    if (hideCurrent) {
      currentCustomerInvalid =
        currentCustomerCondition?.attribute === '' ||
        currentCustomerCondition?.attribute === undefined ||
        currentCustomerCondition?.filterOperation === '' ||
        currentCustomerCondition?.filterOperation === undefined ||
        currentCustomerCondition?.values.length < 1
    }
    if (hideDisqualified) {
      disqualifiedInvalid =
        disqualifiedCondition?.attribute === '' ||
        disqualifiedCondition?.attribute === undefined ||
        disqualifiedCondition?.filterOperation === '' ||
        disqualifiedCondition?.filterOperation === undefined ||
        disqualifiedCondition?.values.length < 1
    }
    if (currentCustomerInvalid || disqualifiedInvalid) {
      setInvalidState(true)
    } else {
      setInvalidState(false)
      onSave()
    }
  }

  const onSave = () => {
    const requestData = buildRequest()
    setShowSaveCancel(false)
    setFiltersUpdated(true)
    setGlobalFiltersSettings(requestData).then(() => success('Advanced preferences saved'))
    segmentTrack(TrackType.GLOBAL_FILTERS_CHANGED, { prevData: defaultData, newData: requestData })
  }

  const onCancel = () => {
    parseSettings(defaultData)
    setShowSaveCancel(false)
    setInvalidState(false)
  }

  return {
    inCRM,
    setInCRM,
    hideCurrent,
    setHideCurrent,
    currentCustomerCondition,
    setCurrentCustomerCondition,
    hideDisqualified,
    setHideDisqualified,
    disqualifiedCondition,
    setDisqualifiedCondition,
    onCancel,
    showSaveCancel,
    setShowSaveCancel,
    validateToSave,
    invalidState
  }
}
